<template>
  <div class="calendar-view">
    <!-- Calendar Header -->
    <div class="calendar-header d-flex justify-content-between align-items-center mb-3">
      <button @click="previousMonth" class="btn btn-sm btn-outline-primary">
        <AppIcon name="bi-chevron-left" class="bi bi-chevron-left" />
      </button>
      <h5 class="mb-0">{{ currentMonthYear }}</h5>
      <button @click="nextMonth" class="btn btn-sm btn-outline-primary">
        <AppIcon name="bi-chevron-right" class="bi bi-chevron-right" />
      </button>
    </div>

    <!-- Legend -->
    <div class="d-flex justify-content-center gap-3 mb-3 flex-wrap">
      <div v-for="status in availableStatuses" :key="status.value" class="d-flex align-items-center">
        <div class="legend-box" :style="{ borderLeftColor: status.color, backgroundColor: status.color + '20' }"></div>
        <small>{{ status.label }}</small>
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="calendar-grid">
      <!-- Day Headers -->
      <div v-for="day in dayNames" :key="day" class="calendar-day-header">
        {{ day }}
      </div>

      <!-- Calendar Days -->
      <div 
        v-for="(day, index) in calendarDays" 
        :key="index"
        :class="['calendar-day', {
          'other-month': !day.isCurrentMonth,
          'today': day.isToday
        }]"
      >
        <div class="day-number">{{ day.date }}</div>
        <div class="day-tasks">
          <div 
            v-for="assignment in day.tasks" 
            :key="assignment.id"
            :class="'task-item'"
            :style="{ borderLeftColor: getStatusColor(assignment.task.status), backgroundColor: getStatusColor(assignment.task.status) + '20' }"
            @click="$emit('view', assignment.task)"
          >
            <div class="task-item-content">
              <span :class="'badge bg-' + getPriorityColor(assignment.task.priority)" style="font-size: 8px;">!</span>
              <small class="task-item-title" draggable="false" style="cursor: pointer; text-decoration: underline;" @click.stop="$emit('view', assignment.task)">{{ assignment.task.title }}</small>
            </div>
            <div v-if="assignment.task.project" class="task-project">
              <small>{{ assignment.task.project.name }}</small>
            </div>
          </div>
          <div v-if="day.tasks.length > 3" class="text-muted small text-center mt-1">
            +{{ day.tasks.length - 3 }} more
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import helper from '../../helper'
import { getTaskStatuses, getTaskStatusColor, getTaskStatusLabel } from '../../helpers/taskStatusHelper'

export default {
  name: 'MyTaskCalendar',
  components: {
  },
  props: {
    tasks: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentDate: new Date(),
      dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      helper
    }
  },
  computed: {
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    },
    availableStatuses() {
      return getTaskStatuses(this.$store);
    },
    calendarDays() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      
      // First day of the month
      const firstDay = new Date(year, month, 1)
      const firstDayOfWeek = firstDay.getDay()
      
      // Last day of the month
      const lastDay = new Date(year, month + 1, 0)
      const lastDate = lastDay.getDate()
      
      // Previous month days
      const prevMonthLastDay = new Date(year, month, 0).getDate()
      
      const days = []
      
      // Add previous month days
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = prevMonthLastDay - i
        days.push({
          date,
          fullDate: new Date(year, month - 1, date),
          isCurrentMonth: false,
          isToday: false,
          tasks: []
        })
      }
      
      // Add current month days
      const today = new Date()
      for (let date = 1; date <= lastDate; date++) {
        const fullDate = new Date(year, month, date)
        const isToday = fullDate.toDateString() === today.toDateString()
        days.push({
          date,
          fullDate,
          isCurrentMonth: true,
          isToday,
          tasks: this.getTasksForDate(fullDate)
        })
      }
      
      // Add next month days to complete the grid
      const remainingDays = 42 - days.length // 6 rows * 7 days
      for (let date = 1; date <= remainingDays; date++) {
        days.push({
          date,
          fullDate: new Date(year, month + 1, date),
          isCurrentMonth: false,
          isToday: false,
          tasks: []
        })
      }
      
      return days
    }
  },
  methods: {
    getTasksForDate(date) {
      return this.tasks.filter(assignment => {
        if (!assignment.task || !assignment.task.due_date) return false
        const taskDate = new Date(assignment.task.due_date)
        return taskDate.toDateString() === date.toDateString()
      }).slice(0, 3) // Show max 3 tasks per day
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'info',
        medium: 'primary',
        high: 'warning',
        urgent: 'danger'
      }
      return colors[priority] || 'primary'
    },
    getStatusColor(status) {
      return getTaskStatusColor(this.$store, status);
    },
    getStatusLabel(status) {
      return getTaskStatusLabel(this.$store, status);
    },
    previousMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1)
    },
    nextMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1)
    }
  }
}
</script>

<style scoped>
.calendar-view {
  width: 100%;
}

.legend-box {
  width: 20px;
  height: 12px;
  border-radius: 3px;
  margin-right: 5px;
  border-left: 3px solid;
}

.gap-3 {
  gap: 1rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #dee2e6;
  border: 1px solid #dee2e6;
}

.calendar-day-header {
  background: #f8f9fa;
  padding: 10px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
  color: #495057;
}

.calendar-day {
  background: white;
  min-height: 100px;
  padding: 5px;
  position: relative;
}

.calendar-day.other-month {
  background: #f8f9fa;
  opacity: 0.5;
}

.calendar-day.today {
  background: #e7f3ff;
}

.day-number {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.calendar-day.today .day-number {
  background: #0d6efd;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.day-tasks {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.task-item {
  padding: 3px 5px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  border-left: 3px solid;
}

.task-item:hover {
  opacity: 0.8;
  transform: translateX(2px);
}

.task-item-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.task-item-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.task-project {
  font-size: 9px;
  color: #6c757d;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-todo {
  background: #e9ecef;
  border-left-color: #6c757d;
}

.task-in_progress {
  background: #cfe2ff;
  border-left-color: #0d6efd;
}

.task-review {
  background: #fff3cd;
  border-left-color: #ffc107;
}

.task-completed {
  background: #d1e7dd;
  border-left-color: #198754;
}

.task-cancelled {
  background: #f8d7da;
  border-left-color: #dc3545;
}

@media (max-width: 768px) {
  .calendar-day {
    min-height: 80px;
  }
  
  .day-number {
    font-size: 12px;
  }
  
  .task-item {
    font-size: 10px;
  }
}
</style>
